import { nextFreeUploadName, type IngestBatchSkipReason } from '@kennisbank/shared';

/**
 * Bulk / folder upload — pure decision helpers for the pre-flight step.
 *
 * Input: the planned files (already filtered + renamed by the shared
 * `planUploadNames`) with their content md5, plus the server's
 * `/documents/check-duplicates` answer. Output: per file, upload / skip /
 * ask-the-admin — the default policy is "skip if identical content,
 * otherwise ask" — and, once the admin has answered, the final upload list.
 */

export interface PlannedFingerprint {
  finalName: string;
  md5Base64: string;
}

export interface DuplicateCheckItem {
  filename: string;
  existing: { contentMd5: string | null; sameContent: boolean } | null;
  identicalTo: string[];
}

export type Disposition =
  | { kind: 'upload' }
  | { kind: 'skip'; reason: IngestBatchSkipReason; detail: string }
  /** Name exists in the target ring with different (or unknown) content. */
  | { kind: 'ask' };

export type CollisionChoice = 'overwrite' | 'skip' | 'keep_both';

export function classifyPlanned(
  planned: readonly PlannedFingerprint[],
  checks: readonly DuplicateCheckItem[],
): Disposition[] {
  const byName = new Map(checks.map((c) => [c.filename, c]));
  const firstByMd5 = new Map<string, string>();
  return planned.map((p) => {
    const earlier = firstByMd5.get(p.md5Base64);
    if (earlier !== undefined) {
      return {
        kind: 'skip',
        reason: 'duplicate_in_drop',
        detail: `Same content as ${earlier} in this upload.`,
      };
    }
    firstByMd5.set(p.md5Base64, p.finalName);
    const check = byName.get(p.finalName);
    if (check?.existing?.sameContent) {
      return {
        kind: 'skip',
        reason: 'identical_existing',
        detail: 'Already in the knowledgebase with identical content.',
      };
    }
    const elsewhere = check?.identicalTo.filter((n) => n !== p.finalName) ?? [];
    if (elsewhere.length > 0) {
      return {
        kind: 'skip',
        reason: 'identical_existing',
        detail: `Identical to existing document ${elsewhere[0]}.`,
      };
    }
    if (check?.existing) return { kind: 'ask' };
    return { kind: 'upload' };
  });
}

export interface ResolvedUpload {
  plannedIndex: number;
  filename: string;
  overwrite: boolean;
  /** Set when "Keep both" renamed the file away from an existing name. */
  renamedFrom?: string;
}

export interface ResolvedSkip {
  plannedIndex: number;
  reason: IngestBatchSkipReason;
  detail: string;
}

/**
 * Apply the admin's collision answers. `takenNames` must hold the lower-cased
 * names already in the registry that the page knows about; "Keep both" picks
 * `name (2).ext`-style names clear of those AND of every other file in this
 * upload. Returns null while any `ask` is still unanswered.
 */
export function resolveUploads(
  planned: readonly PlannedFingerprint[],
  dispositions: readonly Disposition[],
  choices: ReadonlyMap<number, CollisionChoice>,
  takenNames: ReadonlySet<string>,
): { uploads: ResolvedUpload[]; skipped: ResolvedSkip[] } | null {
  const taken = new Set(takenNames);
  for (const p of planned) taken.add(p.finalName.toLowerCase());
  const uploads: ResolvedUpload[] = [];
  const skipped: ResolvedSkip[] = [];
  for (let i = 0; i < planned.length; i += 1) {
    const d = dispositions[i];
    const name = planned[i].finalName;
    if (d.kind === 'skip') {
      skipped.push({ plannedIndex: i, reason: d.reason, detail: d.detail });
      continue;
    }
    if (d.kind === 'upload') {
      uploads.push({ plannedIndex: i, filename: name, overwrite: false });
      continue;
    }
    const choice = choices.get(i);
    if (!choice) return null;
    if (choice === 'skip') {
      skipped.push({
        plannedIndex: i,
        reason: 'collision_skipped',
        detail: 'A document with this name exists; you chose Skip.',
      });
    } else if (choice === 'overwrite') {
      uploads.push({ plannedIndex: i, filename: name, overwrite: true });
    } else {
      const renamed = nextFreeUploadName(name, taken);
      taken.add(renamed.toLowerCase());
      uploads.push({ plannedIndex: i, filename: renamed, overwrite: false, renamedFrom: name });
    }
  }
  return { uploads, skipped };
}

export function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Run `fn` over `items` with at most `limit` in flight; preserves no order. */
export async function runWithConcurrency<T>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<void>,
): Promise<void> {
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const item = items[next];
      next += 1;
      await fn(item);
    }
  });
  await Promise.all(workers);
}
